import * as React from 'react';

// Utils
import { navigate, isCurrentPath } from '@toolkit/util/app';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectors } from '@src/shared/src';
import { IRootState } from '@src/store';
import {
  containsTransportSearch,
  isMultiCitySearch,
  isRentalOnlySearch,
  isRoundtripSearch,
  shouldSearchForHotels,
} from '@src/shared/src/util/search';
import { verifyProperty } from '@src/shared/src/util/general';
// Constants
import { ROUTES } from '@toolkit/const/app';
// Components
import BreadcrumbItem from './BreadcrumbItem';
// Styles
import '../styles/Breadcrumbs.scss';

const Breadcrumbs: React.FC = () => {
  const { t } = useTranslation();
  const search = useSelector((state: IRootState) => selectors.search.search(state.search));
  const basket = useSelector((state: IRootState) => state.checkout.basket);

  const isBasketEditable = verifyProperty(true, basket, ['editable']);

  const isPathPastPrepare = () =>
    isCurrentPath('/booking/prepare') ||
    isCurrentPath('/booking/payment') ||
    isCurrentPath('/booking/confirm');

  const isInboundLinkActive: boolean =
    !isPathPastPrepare() &&
    (!isBasketEditable || isCurrentPath(ROUTES.HOTELS) || isCurrentPath('/booking/'));

  const isHotelLinkActive: boolean =
    !isPathPastPrepare() && (!isBasketEditable || isCurrentPath('/booking/'));

  const isCheckoutStepActive: boolean = isCurrentPath('/booking/');

  const isOutboundLinkActive = !isPathPastPrepare() && !isCurrentPath(ROUTES.TRIPS.OUTWARD);
  let HeaderRoutes =
    isCurrentPath(ROUTES.TRIPS.OUTWARD) ||
    isCurrentPath(ROUTES.TRIPS.INBOUND) ||
    isCurrentPath(ROUTES.HOTELS) ||
    isCurrentPath('/booking/') && !isCurrentPath('/booking/confirm');

  if (HeaderRoutes) {
    return (
      <ol className="breadcrumbs">
        {containsTransportSearch(search) && (
          <BreadcrumbItem
            label={
              isMultiCitySearch(search)
                ? `${t('HeaderComponent.link.route')} 1`
                : t('HeaderComponent.link.outbound')
            }
            activeClassName="breadcrumbs-item-active"
            isActive={isCurrentPath(ROUTES.TRIPS.OUTWARD)}
            onClick={
              isOutboundLinkActive ? () => navigate(`${ROUTES.TRIPS.OUTWARD}${search.id}`) : null
            }
          />
        )}

        {(isMultiCitySearch(search) || isRoundtripSearch(search)) && (
          <BreadcrumbItem
            label={
              isMultiCitySearch(search)
                ? `${t('HeaderComponent.link.route')} 2`
                : t('HeaderComponent.link.inbound')
            }
            activeClassName="breadcrumbs-item-active"
            isActive={isCurrentPath(ROUTES.TRIPS.INBOUND)}
            onClick={
              isInboundLinkActive ? () => navigate(`${ROUTES.TRIPS.INBOUND}${search.id}`) : null
            }
          />
        )}

        {shouldSearchForHotels(search) && (
          <BreadcrumbItem
            label={t('HeaderComponent.link.hotel')}
            activeClassName="breadcrumbs-item-active"
            isActive={isCurrentPath(ROUTES.HOTELS)}
            onClick={isHotelLinkActive ? () => navigate(`${ROUTES.HOTELS}${search.id}`) : null}
          />
        )}

        {isRentalOnlySearch(search) && (
          <BreadcrumbItem
            label={t('HeaderComponent.link.rental')}
            activeClassName="breadcrumbs-item-active"
            isActive={false}
          />
        )}

        <BreadcrumbItem
          label={t('HeaderComponent.link.checkout')}
          activeClassName="breadcrumbs-item-active"
          isActive={isCheckoutStepActive}
        />
      </ol>
    );
  }
};

export default Breadcrumbs;
